/*
 * Copyright The OpenTelemetry Authors
 * SPDX-License-Identifier: Apache-2.0
 */

import { diagLogLevelFromString, getStringFromEnv } from '@opentelemetry/core';
import type {
  AttributeLimits,
  Propagator,
  ConfigurationModel,
} from './models/configModel';
import { initializeDefaultConfiguration } from './models/configModel';
import type { ConfigFactory } from './IConfigFactory';
import * as fs from 'fs';
import * as yaml from 'yaml';
import {
  getBooleanFromConfigFile,
  getBooleanListFromConfigFile,
  getGrpcTlsConfig,
  getHttpTlsConfig,
  getNumberFromConfigFile,
  getNumberListFromConfigFile,
  getStringFromConfigFile,
  getStringListFromConfigFile,
} from './utils';
import type { NameStringValuePair } from './models/commonModel';
import { OtlpHttpEncoding, SeverityNumber } from './models/commonModel';
import type {
  ExperimentalComposableSampler,
  Sampler,
  SpanExporter,
  SpanProcessor,
  TracerProvider,
} from './models/tracerProviderModel';
import { initializeDefaultTracerProviderConfiguration } from './models/tracerProviderModel';
import type {
  ExperimentalLoggerMatcherAndConfig,
  LoggerProvider,
  LogRecordExporter,
  LogRecordProcessor,
} from './models/loggerProviderModel';
import { initializeDefaultLoggerProviderConfiguration } from './models/loggerProviderModel';
import type {
  AttributeNameValue,
  ExperimentalResourceDetection,
  ExperimentalResourceDetector,
} from './models/resourceModel';
import type {
  Aggregation,
  CardinalityLimits,
  MeterProvider,
  MetricProducer,
  MetricReader,
  PullMetricExporter,
  PushMetricExporter,
  View,
  ViewSelector,
  ViewStream,
} from './models/meterProviderModel';
import {
  ExemplarFilter,
  ExperimentalPrometheusTranslationStrategy,
  ExporterDefaultHistogramAggregation,
  ExporterTemporalityPreference,
  initializeDefaultMeterProviderConfiguration,
  InstrumentType,
} from './models/meterProviderModel';
import { diag } from '@opentelemetry/api';

export class FileConfigFactory implements ConfigFactory {
  private _config: ConfigurationModel;

  constructor() {
    this._config = initializeDefaultConfiguration();
    parseConfigFile(this._config);
  }

  getConfigModel(): ConfigurationModel {
    return this._config;
  }
}

export function hasValidConfigFile(): boolean {
  const configFile = getStringFromEnv('OTEL_CONFIG_FILE');
  if (configFile) {
    if (
      !(configFile.endsWith('.yaml') || configFile.endsWith('.yml')) ||
      !fs.existsSync(configFile)
    ) {
      diag.warn(
        `Config file ${configFile} set on OTEL_CONFIG_FILE is not valid`
      );
      return false;
    }
    return true;
  }
  return false;
}

export function parseConfigFile(config: ConfigurationModel) {
  const supportedFileVersions = ['1.0-rc.3'];
  const configFile = getStringFromEnv('OTEL_CONFIG_FILE') || '';
  const file = fs.readFileSync(configFile, 'utf8');
  const parsedContent = yaml.parse(file);

  if (
    parsedContent['file_format'] &&
    supportedFileVersions.includes(parsedContent['file_format'])
  ) {
    const disabled = getBooleanFromConfigFile(parsedContent['disabled']);
    if (disabled !== undefined) {
      config.disabled = disabled;
    }

    const logLevel = getNumberFromConfigFile(
      diagLogLevelFromString(parsedContent['log_level'])
    );
    if (logLevel) {
      config.log_level = logLevel;
    }

    if (parsedContent['resource']) {
      if (config.resource == null) {
        config.resource = {};
      }
      const schemaUrl = getStringFromConfigFile(
        parsedContent['resource']?.['schema_url']
      );
      if (schemaUrl) {
        config.resource.schema_url = schemaUrl;
      }
    }

    setResourceAttributes(
      config,
      parsedContent['resource']?.['attributes'],
      parsedContent['resource']?.['attributes_list']
    );

    const detectionConfig =
      parsedContent['resource']?.['detection/development'];
    if (detectionConfig) {
      config.resource!['detection/development'] =
        parseDetectionDevelopment(detectionConfig);
    }
    setAttributeLimits(config, parsedContent['attribute_limits']);
    setPropagator(config, parsedContent['propagator']);
    setTracerProvider(config, parsedContent['tracer_provider']);
    setMeterProvider(config, parsedContent['meter_provider']);
    setLoggerProvider(config, parsedContent['logger_provider']);
  } else {
    diag.warn(
      `Unsupported File Format: ${parsedContent['file_format']}. It must be one of the following: ${supportedFileVersions}`
    );
  }
}

export function setResourceAttributes(
  config: ConfigurationModel,
  attributes: AttributeNameValue[],
  attributeList: string
) {
  if (attributes || attributeList) {
    const addedKeys = [];
    if (config.resource == null) {
      config.resource = {};
    }
    if (getStringFromConfigFile(attributeList)) {
      config.resource.attributes_list = getStringFromConfigFile(attributeList);
    }

    const list = getStringListFromConfigFile(attributeList);
    if ((list && list.length > 0) || (attributes && attributes.length > 0)) {
      config.resource.attributes = [];

      if (attributes) {
        for (let i = 0; i < attributes.length; i++) {
          const att = attributes[i];
          let value = att['value'];
          switch (att['type']) {
            case 'bool':
              value = getBooleanFromConfigFile(value);
              break;
            case 'bool_array':
              value = getBooleanListFromConfigFile(value);
              break;
            case 'int':
            case 'double':
              value = getNumberFromConfigFile(value);
              break;
            case 'int_array':
            case 'double_array':
              value = getNumberListFromConfigFile(value);
              break;
            case 'string_array':
              value = getStringListFromConfigFile(value);
              break;
            default:
              value = getStringFromConfigFile(value);
              break;
          }

          const key = getStringFromConfigFile(att['name']) ?? '';
          config.resource.attributes.push({
            name: key,
            value: value,
            type: att['type'] ?? 'string',
          });
          addedKeys.push(key);
        }
      }

      if (list) {
        for (let i = 0; i < list.length; i++) {
          const element = list[i].split('=');
          if (!addedKeys.includes(element[0])) {
            config.resource.attributes.push({
              name: element[0],
              value: element[1],
              type: 'string',
            });
          }
        }
      }
    }
  }
}

function parseDetectionDevelopment(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  detection: any
): ExperimentalResourceDetection {
  const result: ExperimentalResourceDetection = {};

  if (detection['attributes']) {
    result.attributes = {};
    const included = detection['attributes']['included'];
    if (Array.isArray(included)) {
      result.attributes.included = included.filter(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (v: any) => typeof v === 'string'
      );
    }
    const excluded = detection['attributes']['excluded'];
    if (Array.isArray(excluded)) {
      result.attributes.excluded = excluded.filter(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (v: any) => typeof v === 'string'
      );
    }
  }

  if (Array.isArray(detection['detectors'])) {
    result.detectors = [];
    for (let i = 0; i < detection['detectors'].length; i++) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const d: any = detection['detectors'][i];
      if (typeof d !== 'object' || d === null) {
        continue;
      }
      const detector: ExperimentalResourceDetector = {};
      if ('container' in d) detector.container = d.container ?? {};
      if ('env' in d) detector.env = d.env ?? {};
      if ('host' in d) detector.host = d.host ?? {};
      if ('os' in d) detector.os = d.os ?? {};
      if ('process' in d) detector.process = d.process ?? {};
      if ('service' in d) detector.service = d.service ?? {};
      result.detectors.push(detector);
    }
  }

  return result;
}

export function setAttributeLimits(
  config: ConfigurationModel,
  attrLimits: AttributeLimits
) {
  if (attrLimits) {
    if (config.attribute_limits == null) {
      config.attribute_limits = {
        attribute_count_limit: 128,
      };
    }
    const lengthLimit = getNumberFromConfigFile(
      attrLimits['attribute_value_length_limit']
    );
    if (lengthLimit) {
      config.attribute_limits.attribute_value_length_limit = lengthLimit;
    }
    const countLimit = getNumberFromConfigFile(
      attrLimits['attribute_count_limit']
    );
    if (countLimit) {
      config.attribute_limits.attribute_count_limit = countLimit;
    }
  }
}

export function setPropagator(
  config: ConfigurationModel,
  propagator: Propagator
): void {
  if (propagator && (propagator.composite || propagator.composite_list)) {
    const auxList = [];
    const composite = [];
    if (propagator.composite) {
      for (let i = 0; i < propagator.composite.length; i++) {
        const key = Object.keys(propagator.composite[i])[0];
        auxList.push(key);
        composite.push({ [key]: null });
      }
    }

    const compositeList = getStringListFromConfigFile(
      propagator['composite_list']
    );
    if (compositeList) {
      for (let i = 0; i < compositeList.length; i++) {
        if (!auxList.includes(compositeList[i])) {
          composite.push({ [compositeList[i]]: null });
        }
      }
    }
    if (composite.length > 0) {
      if (config.propagator == null) {
        config.propagator = {};
      }
      config.propagator.composite = composite;
    }

    const compositeListString = getStringFromConfigFile(
      propagator['composite_list']
    );
    if (compositeListString) {
      config.propagator!.composite_list = compositeListString;
    } else if (auxList.length > 0) {
      // Generate composite_list from the composite entries
      config.propagator!.composite_list = auxList.join(',');
    }
  }
}

function parseComposableSampler(
  sampler: ExperimentalComposableSampler
): ExperimentalComposableSampler {
  const samplerType = Object.keys(sampler)[0];
  let parsedSampler: ExperimentalComposableSampler = {};

  switch (samplerType) {
    case 'always_on':
      parsedSampler = { always_on: sampler['always_on'] ?? undefined };
      break;

    case 'always_off':
      parsedSampler = { always_off: sampler['always_off'] ?? undefined };
      break;

    case 'parent_threshold': {
      const s = sampler['parent_threshold'];
      if (s?.root) {
        parsedSampler = {
          parent_threshold: { root: parseComposableSampler(s.root) },
        };
      }
      break;
    }

    case 'probability': {
      const s = sampler['probability'];
      parsedSampler = {
        probability: {
          ratio: getNumberFromConfigFile(s?.ratio) ?? 1.0,
        },
      };
      break;
    }

    case 'rule_based': {
      const rb = sampler['rule_based'];
      if (rb) {
        parsedSampler = { rule_based: {} };
        if (rb.rules) {
          parsedSampler.rule_based!.rules = rb.rules.map(rule => ({
            ...rule,
            sampler: rule.sampler ? parseComposableSampler(rule.sampler) : {},
          }));
        }
      }
      break;
    }
  }

  return parsedSampler;
}

function parseSampler(sampler: Sampler): Sampler {
  const samplerType = Object.keys(sampler)[0];
  let parsedSampler: Sampler = {};

  switch (samplerType) {
    case 'always_on':
      parsedSampler = { always_on: sampler['always_on'] ?? undefined };
      break;

    case 'always_off':
      parsedSampler = { always_off: sampler['always_off'] ?? undefined };
      break;

    case 'trace_id_ratio_based': {
      const s = sampler['trace_id_ratio_based'];
      parsedSampler = {
        trace_id_ratio_based: {
          ratio: getNumberFromConfigFile(s?.ratio) ?? 1.0,
        },
      };
      break;
    }

    case 'parent_based': {
      const s = sampler['parent_based'];
      if (s) {
        parsedSampler = { parent_based: {} };
        if (s.root) {
          parsedSampler.parent_based!.root = parseSampler(s.root);
        }
        if (s.remote_parent_sampled) {
          parsedSampler.parent_based!.remote_parent_sampled = parseSampler(
            s.remote_parent_sampled
          );
        }
        if (s.remote_parent_not_sampled) {
          parsedSampler.parent_based!.remote_parent_not_sampled = parseSampler(
            s.remote_parent_not_sampled
          );
        }
        if (s.local_parent_sampled) {
          parsedSampler.parent_based!.local_parent_sampled = parseSampler(
            s.local_parent_sampled
          );
        }
        if (s.local_parent_not_sampled) {
          parsedSampler.parent_based!.local_parent_not_sampled = parseSampler(
            s.local_parent_not_sampled
          );
        }
      }
      break;
    }

    case 'probability/development': {
      const s = sampler['probability/development'];
      parsedSampler = {
        'probability/development': {
          ratio: getNumberFromConfigFile(s?.ratio) ?? 1.0,
        },
      };
      break;
    }

    case 'composite/development': {
      const s = sampler['composite/development'];
      if (s) {
        parsedSampler = {
          'composite/development': parseComposableSampler(s),
        };
      }
      break;
    }
  }

  return parsedSampler;
}

function getConfigHeaders(
  h?: NameStringValuePair[]
): NameStringValuePair[] | null {
  if (h) {
    const headers: NameStringValuePair[] = [];
    for (let i = 0; i < h.length; i++) {
      const element = h[i];
      headers.push({
        name: element['name'],
        value: element['value'],
      });
    }
    if (headers.length > 0) {
      return headers;
    }
  }
  return null;
}

enum ProviderType {
  TRACER = 0,
  METER = 1,
  LOGGER = 2,
}

function parseConfigSpanOrLogRecordExporter(
  exporter: SpanExporter | LogRecordExporter,
  providerType: ProviderType
): SpanExporter | LogRecordExporter {
  const exporterType = Object.keys(exporter)[0];
  let parsedExporter: SpanExporter | LogRecordExporter = {};
  let e;
  let certFile;
  let clientCertFile;
  let clientKeyFile;
  let compression;
  let headers;
  let headersList;
  let insecure;
  let endpoint;

  switch (providerType) {
    case ProviderType.TRACER:
      endpoint = 'http://localhost:4318/v1/traces';
      parsedExporter = parsedExporter as SpanExporter;
      break;
    case ProviderType.LOGGER:
      endpoint = 'http://localhost:4318/v1/logs';
      parsedExporter = parsedExporter as LogRecordExporter;
      break;
  }

  switch (exporterType) {
    case 'otlp_http':
      e = exporter['otlp_http'];
      if (e) {
        parsedExporter = {
          otlp_http: {
            endpoint: getStringFromConfigFile(e['endpoint']) ?? endpoint,
            timeout: getNumberFromConfigFile(e['timeout']) ?? 10000,
            encoding:
              getStringFromConfigFile(e['encoding']) === 'json'
                ? OtlpHttpEncoding.JSON
                : OtlpHttpEncoding.Protobuf,
          },
        };
        compression = getStringFromConfigFile(e['compression']);
        if (compression && parsedExporter.otlp_http) {
          parsedExporter.otlp_http.compression = compression;
        }
        headersList = getStringFromConfigFile(e['headers_list']);
        if (headersList && parsedExporter.otlp_http) {
          parsedExporter.otlp_http.headers_list = headersList;
        }
        headers = getConfigHeaders(e['headers']);
        if (headers && parsedExporter.otlp_http) {
          parsedExporter.otlp_http.headers = headers;
        }

        if (e['tls']) {
          certFile = getStringFromConfigFile(e['tls']['ca_file']);
          clientCertFile = getStringFromConfigFile(e['tls']['cert_file']);
          clientKeyFile = getStringFromConfigFile(e['tls']['key_file']);

          const tls = getHttpTlsConfig(certFile, clientKeyFile, clientCertFile);
          if (tls) {
            parsedExporter.otlp_http!.tls = tls;
          }
        }
      }
      break;

    case 'otlp_grpc':
      e = exporter['otlp_grpc'];
      if (e) {
        parsedExporter = {
          otlp_grpc: {
            endpoint:
              getStringFromConfigFile(e['endpoint']) ?? 'http://localhost:4317',
            timeout: getNumberFromConfigFile(e['timeout']) ?? 10000,
          },
        };

        compression = getStringFromConfigFile(e['compression']);
        if (compression && parsedExporter.otlp_grpc) {
          parsedExporter.otlp_grpc.compression = compression;
        }
        headersList = getStringFromConfigFile(e['headers_list']);
        if (headersList && parsedExporter.otlp_grpc) {
          parsedExporter.otlp_grpc.headers_list = headersList;
        }
        headers = getConfigHeaders(e['headers']);
        if (headers && parsedExporter.otlp_grpc) {
          parsedExporter.otlp_grpc.headers = headers;
        }

        if (e['tls']) {
          certFile = getStringFromConfigFile(e['tls']['ca_file']);
          clientCertFile = getStringFromConfigFile(e['tls']['cert_file']);
          clientKeyFile = getStringFromConfigFile(e['tls']['key_file']);
          insecure = getBooleanFromConfigFile(e['tls']['insecure']);

          const tls = getGrpcTlsConfig(
            certFile,
            clientKeyFile,
            clientCertFile,
            insecure
          );
          if (tls) {
            parsedExporter.otlp_grpc!.tls = tls;
          }
        }
      }
      break;

    case 'otlp_file/development':
      e = exporter['otlp_file/development'];
      if (e) {
        parsedExporter = {
          'otlp_file/development': {
            output_stream:
              getStringFromConfigFile(e['output_stream']) ?? 'stdout',
          },
        };
      }
      break;

    case 'console':
      parsedExporter = {
        console: {},
      };
      break;
  }

  return parsedExporter;
}

export function setTracerProvider(
  config: ConfigurationModel,
  tracerProvider: TracerProvider
): void {
  if (tracerProvider && tracerProvider.processors?.length > 0) {
    config.tracer_provider = initializeDefaultTracerProviderConfiguration();
    // Limits
    if (tracerProvider['limits']) {
      const attributeValueLengthLimit = getNumberFromConfigFile(
        tracerProvider['limits']['attribute_value_length_limit']
      );

      if (attributeValueLengthLimit) {
        config.tracer_provider.limits!.attribute_value_length_limit =
          attributeValueLengthLimit;
      }

      const attributeCountLimit = getNumberFromConfigFile(
        tracerProvider['limits']['attribute_count_limit']
      );
      if (attributeCountLimit) {
        config.tracer_provider.limits!.attribute_count_limit =
          attributeCountLimit;
      }

      const eventCountLimit = getNumberFromConfigFile(
        tracerProvider['limits']['event_count_limit']
      );
      if (eventCountLimit) {
        config.tracer_provider.limits!.event_count_limit = eventCountLimit;
      }

      const linkCountLimit = getNumberFromConfigFile(
        tracerProvider['limits']['link_count_limit']
      );
      if (linkCountLimit) {
        config.tracer_provider.limits!.link_count_limit = linkCountLimit;
      }

      const eventAttributeCountLimit = getNumberFromConfigFile(
        tracerProvider['limits']['event_attribute_count_limit']
      );
      if (eventAttributeCountLimit) {
        config.tracer_provider.limits!.event_attribute_count_limit =
          eventAttributeCountLimit;
      }

      const linkAttributeCountLimit = getNumberFromConfigFile(
        tracerProvider['limits']['link_attribute_count_limit']
      );
      if (linkAttributeCountLimit) {
        config.tracer_provider.limits!.link_attribute_count_limit =
          linkAttributeCountLimit;
      }
    }

    // Sampler
    if (tracerProvider['sampler']) {
      config.tracer_provider.sampler = parseSampler(tracerProvider['sampler']);
    }

    // Processors
    for (let i = 0; i < tracerProvider['processors'].length; i++) {
      const processorType = Object.keys(tracerProvider['processors'][i])[0];
      if (processorType === 'batch') {
        const element = tracerProvider['processors'][i]['batch'];
        if (element) {
          const parsedExporter = parseConfigSpanOrLogRecordExporter(
            element['exporter'],
            ProviderType.TRACER
          );
          const batchConfig: SpanProcessor = {
            batch: {
              schedule_delay:
                getNumberFromConfigFile(element['schedule_delay']) ?? 5000,
              export_timeout:
                getNumberFromConfigFile(element['export_timeout']) ?? 30000,
              max_queue_size:
                getNumberFromConfigFile(element['max_queue_size']) ?? 2048,
              max_export_batch_size:
                getNumberFromConfigFile(element['max_export_batch_size']) ??
                512,
              exporter: parsedExporter as SpanExporter,
            },
          };

          config.tracer_provider.processors.push(batchConfig);
        }
      } else if (processorType === 'simple') {
        const element = tracerProvider['processors'][i]['simple'];
        if (element) {
          const parsedExporter = parseConfigSpanOrLogRecordExporter(
            element['exporter'],
            ProviderType.TRACER
          );
          const simpleConfig: SpanProcessor = {
            simple: {
              exporter: parsedExporter as SpanExporter,
            },
          };

          config.tracer_provider.processors.push(simpleConfig);
        }
      }
    }
  } else if (
    tracerProvider &&
    (tracerProvider.processors == null ||
      tracerProvider.processors.length === 0)
  ) {
    diag.warn('TracerProvider must have at least one processor configured');
  }
}

function getCardinalityLimits(limits?: CardinalityLimits): CardinalityLimits {
  if (limits == null) {
    limits = {};
  }
  const defaultLimit = getNumberFromConfigFile(limits['default']) ?? 2000;

  return {
    default: defaultLimit,
    counter: getNumberFromConfigFile(limits['counter']) ?? defaultLimit,
    gauge: getNumberFromConfigFile(limits['gauge']) ?? defaultLimit,
    histogram: getNumberFromConfigFile(limits['histogram']) ?? defaultLimit,
    observable_counter:
      getNumberFromConfigFile(limits['observable_counter']) ?? defaultLimit,
    observable_gauge:
      getNumberFromConfigFile(limits['observable_gauge']) ?? defaultLimit,
    observable_up_down_counter:
      getNumberFromConfigFile(limits['observable_up_down_counter']) ??
      defaultLimit,
    up_down_counter:
      getNumberFromConfigFile(limits['up_down_counter']) ?? defaultLimit,
  };
}

function getProducers(producers?: MetricProducer[]): MetricProducer[] {
  const parsedProducers: MetricProducer[] = [];
  if (producers) {
    for (let j = 0; j < producers.length; j++) {
      const producer = producers[j];
      if (Object.keys(producer)[0] === 'opencensus') {
        parsedProducers.push({ opencensus: {} });
      }
    }
  }

  return parsedProducers;
}

export function getTemporalityPreference(
  temporalityPreference?: ExporterTemporalityPreference
): ExporterTemporalityPreference {
  const temporalityPreferenceType = getStringFromConfigFile(
    temporalityPreference
  );
  switch (temporalityPreferenceType) {
    case 'cumulative':
      return ExporterTemporalityPreference.Cumulative;
    case 'delta':
      return ExporterTemporalityPreference.Delta;
    case 'low_memory':
      return ExporterTemporalityPreference.LowMemory;
    default:
      return ExporterTemporalityPreference.Cumulative;
  }
}

function getDefaultHistogramAggregation(
  defaultHistogramAggregation?: ExporterDefaultHistogramAggregation
): ExporterDefaultHistogramAggregation {
  const defaultHistogramAggregationType = getStringFromConfigFile(
    defaultHistogramAggregation
  );
  switch (defaultHistogramAggregationType) {
    case 'explicit_bucket_histogram':
      return ExporterDefaultHistogramAggregation.ExplicitBucketHistogram;
    case 'base2_exponential_bucket_histogram':
      return ExporterDefaultHistogramAggregation.Base2ExponentialBucketHistogram;
    default:
      return ExporterDefaultHistogramAggregation.ExplicitBucketHistogram;
  }
}

function parseMetricExporter(exporter: PushMetricExporter): PushMetricExporter {
  const exporterType = Object.keys(exporter)[0];
  let parsedExporter: PushMetricExporter = {};
  let e;
  let certFile;
  let clientCertFile;
  let clientKeyFile;
  let compression;
  let headers;
  let headersList;
  let insecure;

  switch (exporterType) {
    case 'otlp_http':
      e = exporter['otlp_http'];
      if (e) {
        parsedExporter = {
          otlp_http: {
            endpoint:
              getStringFromConfigFile(e['endpoint']) ??
              'http://localhost:4318/v1/metrics',
            timeout: getNumberFromConfigFile(e['timeout']) ?? 10000,
            encoding:
              getStringFromConfigFile(e['encoding']) === 'json'
                ? OtlpHttpEncoding.JSON
                : OtlpHttpEncoding.Protobuf,
            temporality_preference: getTemporalityPreference(
              e['temporality_preference']
            ),
            default_histogram_aggregation: getDefaultHistogramAggregation(
              e['default_histogram_aggregation']
            ),
          },
        };

        compression = getStringFromConfigFile(e['compression']);
        if (compression && parsedExporter.otlp_http) {
          parsedExporter.otlp_http.compression = compression;
        }
        headersList = getStringFromConfigFile(e['headers_list']);
        if (headersList && parsedExporter.otlp_http) {
          parsedExporter.otlp_http.headers_list = headersList;
        }
        headers = getConfigHeaders(e['headers']);
        if (headers && parsedExporter.otlp_http) {
          parsedExporter.otlp_http.headers = headers;
        }

        if (e['tls']) {
          certFile = getStringFromConfigFile(e['tls']['ca_file']);
          clientCertFile = getStringFromConfigFile(e['tls']['cert_file']);
          clientKeyFile = getStringFromConfigFile(e['tls']['key_file']);

          const tls = getHttpTlsConfig(certFile, clientKeyFile, clientCertFile);
          if (tls) {
            parsedExporter.otlp_http!.tls = tls;
          }
        }
      }
      break;

    case 'otlp_grpc':
      e = exporter['otlp_grpc'];
      if (e) {
        parsedExporter = {
          otlp_grpc: {
            endpoint:
              getStringFromConfigFile(e['endpoint']) ?? 'http://localhost:4317',
            timeout: getNumberFromConfigFile(e['timeout']) ?? 10000,
            temporality_preference: getTemporalityPreference(
              e['temporality_preference']
            ),
            default_histogram_aggregation: getDefaultHistogramAggregation(
              e['default_histogram_aggregation']
            ),
          },
        };

        compression = getStringFromConfigFile(e['compression']);
        if (compression && parsedExporter.otlp_grpc) {
          parsedExporter.otlp_grpc.compression = compression;
        }
        headersList = getStringFromConfigFile(e['headers_list']);
        if (headersList && parsedExporter.otlp_grpc) {
          parsedExporter.otlp_grpc.headers_list = headersList;
        }
        headers = getConfigHeaders(e['headers']);
        if (headers && parsedExporter.otlp_grpc) {
          parsedExporter.otlp_grpc.headers = headers;
        }

        if (e['tls']) {
          certFile = getStringFromConfigFile(e['tls']['ca_file']);
          clientCertFile = getStringFromConfigFile(e['tls']['cert_file']);
          clientKeyFile = getStringFromConfigFile(e['tls']['key_file']);
          insecure = getBooleanFromConfigFile(e['tls']['insecure']);

          const tls = getGrpcTlsConfig(
            certFile,
            clientKeyFile,
            clientCertFile,
            insecure
          );
          if (tls) {
            parsedExporter.otlp_grpc!.tls = tls;
          }
        }
      }
      break;

    case 'otlp_file/development':
      e = exporter['otlp_file/development'];
      if (e) {
        parsedExporter = {
          'otlp_file/development': {
            output_stream:
              getStringFromConfigFile(e['output_stream']) ?? 'stdout',
            temporality_preference: getTemporalityPreference(
              e['temporality_preference']
            ),
            default_histogram_aggregation: getDefaultHistogramAggregation(
              e['default_histogram_aggregation']
            ),
          },
        };
      }
      break;

    case 'console':
      e = exporter['console'];
      if (e) {
        parsedExporter = {
          console: {
            temporality_preference: getTemporalityPreference(
              e['temporality_preference']
            ),
            default_histogram_aggregation: getDefaultHistogramAggregation(
              e['default_histogram_aggregation']
            ),
          },
        };
      }
      break;
  }

  return parsedExporter;
}

export function setMeterProvider(
  config: ConfigurationModel,
  meterProvider: MeterProvider
): void {
  if (meterProvider && meterProvider.readers?.length > 0) {
    config.meter_provider = initializeDefaultMeterProviderConfiguration();
    const exemplarFilter = getStringFromConfigFile(
      meterProvider['exemplar_filter']
    );
    if (exemplarFilter) {
      switch (exemplarFilter) {
        case 'trace_based':
          config.meter_provider.exemplar_filter = ExemplarFilter.TraceBased;
          break;
        case 'always_on':
          config.meter_provider.exemplar_filter = ExemplarFilter.AlwaysOn;
          break;
        case 'always_off':
          config.meter_provider.exemplar_filter = ExemplarFilter.AlwaysOff;
          break;
        default:
          config.meter_provider.exemplar_filter = ExemplarFilter.TraceBased;
          break;
      }
    }

    for (let i = 0; i < meterProvider.readers.length; i++) {
      const readerType = Object.keys(meterProvider['readers'][i])[0];
      if (readerType === 'pull') {
        const element = meterProvider['readers'][i]['pull'];
        if (element) {
          const exporter: PullMetricExporter = {
            'prometheus/development': {
              host:
                getStringFromConfigFile(
                  element['exporter']['prometheus/development']['host']
                ) ?? 'localhost',
              port:
                getNumberFromConfigFile(
                  element['exporter']['prometheus/development']['port']
                ) ?? 9464,
              without_scope_info:
                getBooleanFromConfigFile(
                  element['exporter']['prometheus/development'][
                    'without_scope_info'
                  ]
                ) ?? false,
              without_target_info:
                getBooleanFromConfigFile(
                  element['exporter']['prometheus/development'][
                    'without_target_info'
                  ]
                ) ?? false,
            },
          };
          if (
            element['exporter']['prometheus/development'][
              'with_resource_constant_labels'
            ]
          ) {
            exporter['prometheus/development']!.with_resource_constant_labels =
              {
                included:
                  getStringListFromConfigFile(
                    element['exporter']['prometheus/development'][
                      'with_resource_constant_labels'
                    ]?.['included']
                  ) ?? [],
                excluded:
                  getStringListFromConfigFile(
                    element['exporter']['prometheus/development'][
                      'with_resource_constant_labels'
                    ]?.['excluded']
                  ) ?? [],
              };
          }

          if (
            element['exporter']['prometheus/development'][
              'translation_strategy'
            ]
          ) {
            const ts = getStringFromConfigFile(
              element['exporter']['prometheus/development'][
                'translation_strategy'
              ]
            );
            switch (ts) {
              case 'underscore_escaping_with_suffixes':
                exporter['prometheus/development']!.translation_strategy =
                  ExperimentalPrometheusTranslationStrategy.UnderscoreEscapingWithSuffixes;
                break;
              case 'underscore_escaping_without_suffixes':
                exporter['prometheus/development']!.translation_strategy =
                  ExperimentalPrometheusTranslationStrategy.UnderscoreEscapingWithoutSuffixes;
                break;
              case 'no_utf8_escaping_with_suffixes':
                exporter['prometheus/development']!.translation_strategy =
                  ExperimentalPrometheusTranslationStrategy.NoUtf8EscapingWithSuffixes;
                break;
              case 'no_translation':
                exporter['prometheus/development']!.translation_strategy =
                  ExperimentalPrometheusTranslationStrategy.NoTranslation;
                break;
            }
          }
          const pullReader: MetricReader = {
            pull: {
              exporter: exporter,
              cardinality_limits: getCardinalityLimits(
                element['cardinality_limits']
              ),
            },
          };
          const p = getProducers(element['producers']);
          if (p.length > 0 && pullReader.pull) {
            pullReader.pull.producers = p;
          }
          config.meter_provider.readers.push(pullReader);
        }
      } else if (readerType === 'periodic') {
        const element = meterProvider['readers'][i]['periodic'];
        if (element) {
          const parsedExporter = parseMetricExporter(element['exporter']);

          const periodicReader: MetricReader = {
            periodic: {
              exporter: parsedExporter,
              cardinality_limits: getCardinalityLimits(
                element['cardinality_limits']
              ),
              interval: getNumberFromConfigFile(element['interval']) ?? 60000,
              timeout: getNumberFromConfigFile(element['timeout']) ?? 30000,
            },
          };
          const p = getProducers(element['producers']);
          if (p.length > 0 && periodicReader.periodic) {
            periodicReader.periodic.producers = p;
          }
          config.meter_provider.readers.push(periodicReader);
        }
      }
    }

    if (meterProvider['views'] && meterProvider['views'].length > 0) {
      config.meter_provider.views = [];
      for (let j = 0; j < meterProvider['views'].length; j++) {
        const element = meterProvider['views'][j];
        const view: View = {};
        if (element['selector']) {
          const selector: ViewSelector = {};
          const instrumentName = getStringFromConfigFile(
            element['selector']['instrument_name']
          );
          if (instrumentName) {
            selector.instrument_name = instrumentName;
          }

          const unit = getStringFromConfigFile(element['selector']['unit']);
          if (unit) {
            selector.unit = unit;
          }

          const meterName = getStringFromConfigFile(
            element['selector']['meter_name']
          );
          if (meterName) {
            selector.meter_name = meterName;
          }

          const meterVersion = getStringFromConfigFile(
            element['selector']['meter_version']
          );
          if (meterVersion) {
            selector.meter_version = meterVersion;
          }

          const meterSchemaUrl = getStringFromConfigFile(
            element['selector']['meter_schema_url']
          );
          if (meterSchemaUrl) {
            selector.meter_schema_url = meterSchemaUrl;
          }

          const instrumentType = getStringFromConfigFile(
            element['selector']['instrument_type']
          );
          if (instrumentType) {
            switch (instrumentType) {
              case 'counter':
                selector.instrument_type = InstrumentType.Counter;
                break;
              case 'gauge':
                selector.instrument_type = InstrumentType.Gauge;
                break;
              case 'histogram':
                selector.instrument_type = InstrumentType.Histogram;
                break;
              case 'observable_counter':
                selector.instrument_type = InstrumentType.ObservableCounter;
                break;
              case 'observable_gauge':
                selector.instrument_type = InstrumentType.ObservableGauge;
                break;
              case 'observable_up_down_counter':
                selector.instrument_type =
                  InstrumentType.ObservableUpDownCounter;
                break;
              case 'up_down_counter':
                selector.instrument_type = InstrumentType.UpDownCounter;
                break;
            }
          }

          if (Object.keys(selector).length > 0) {
            view.selector = selector;
          }
        }
        if (element['stream']) {
          const stream: ViewStream = {};
          const name = getStringFromConfigFile(element['stream']['name']);
          if (name) {
            stream.name = name;
          }

          const description = getStringFromConfigFile(
            element['stream']['description']
          );
          if (description) {
            stream.description = description;
          }

          const aggregationCardinalityLimit = getNumberFromConfigFile(
            element['stream']['aggregation_cardinality_limit']
          );
          if (aggregationCardinalityLimit) {
            stream.aggregation_cardinality_limit = aggregationCardinalityLimit;
          }

          if (element['stream']['attribute_keys']) {
            stream.attribute_keys = {
              included:
                getStringListFromConfigFile(
                  element['stream']['attribute_keys']['included']
                ) ?? [],
              excluded:
                getStringListFromConfigFile(
                  element['stream']['attribute_keys']['excluded']
                ) ?? [],
            };
          }
          const rawAgg = element['stream']['aggregation'];
          if (rawAgg) {
            const aggregation: Aggregation = {};
            if (rawAgg['default']) {
              aggregation.default = {};
            }
            if (rawAgg['drop']) {
              aggregation.drop = {};
            }
            if (rawAgg['last_value']) {
              aggregation.last_value = {};
            }
            if (rawAgg['sum']) {
              aggregation.sum = {};
            }
            if (rawAgg['explicit_bucket_histogram']) {
              aggregation.explicit_bucket_histogram = {
                boundaries: getNumberListFromConfigFile(
                  rawAgg['explicit_bucket_histogram']['boundaries']
                ) ?? [
                  0, 5, 10, 25, 50, 75, 100, 250, 500, 750, 1000, 2500, 5000,
                  7500, 10000,
                ],
                record_min_max:
                  getBooleanFromConfigFile(
                    rawAgg['explicit_bucket_histogram']['record_min_max']
                  ) === false
                    ? false
                    : true,
              };
            }
            if (rawAgg['base2_exponential_bucket_histogram']) {
              aggregation.base2_exponential_bucket_histogram = {
                record_min_max:
                  getBooleanFromConfigFile(
                    rawAgg['base2_exponential_bucket_histogram'][
                      'record_min_max'
                    ]
                  ) === false
                    ? false
                    : true,
              };
              const maxScale = getNumberFromConfigFile(
                rawAgg['base2_exponential_bucket_histogram']['max_scale']
              );
              if (maxScale) {
                aggregation.base2_exponential_bucket_histogram.max_scale =
                  maxScale;
              }

              const maxSize = getNumberFromConfigFile(
                rawAgg['base2_exponential_bucket_histogram']['max_size']
              );
              if (maxSize) {
                aggregation.base2_exponential_bucket_histogram.max_size =
                  maxSize;
              }
            }
            stream.aggregation = aggregation;
          }
          if (Object.keys(stream).length > 0) {
            view.stream = stream;
          }
        }
        config.meter_provider.views.push(view);
      }
    }
  } else if (
    meterProvider &&
    (meterProvider.readers == null || meterProvider.readers.length === 0)
  ) {
    diag.warn('MeterProvider must have at least one reader configured');
  }
}

export function getSeverity(
  severity?: SeverityNumber
): SeverityNumber | undefined {
  const severityType = getStringFromConfigFile(severity);
  switch (severityType) {
    case 'debug':
      return SeverityNumber.DEBUG;
    case 'debug2':
      return SeverityNumber.DEBUG2;
    case 'debug3':
      return SeverityNumber.DEBUG3;
    case 'debug4':
      return SeverityNumber.DEBUG4;
    case 'info':
      return SeverityNumber.INFO;
    case 'info2':
      return SeverityNumber.INFO2;
    case 'info3':
      return SeverityNumber.INFO3;
    case 'info4':
      return SeverityNumber.INFO4;
    case 'warn':
      return SeverityNumber.WARN;
    case 'warn2':
      return SeverityNumber.WARN2;
    case 'warn3':
      return SeverityNumber.WARN3;
    case 'warn4':
      return SeverityNumber.WARN4;
    case 'error':
      return SeverityNumber.ERROR;
    case 'error2':
      return SeverityNumber.ERROR2;
    case 'error3':
      return SeverityNumber.ERROR3;
    case 'error4':
      return SeverityNumber.ERROR4;
    case 'fatal':
      return SeverityNumber.FATAL;
    case 'fatal2':
      return SeverityNumber.FATAL2;
    case 'fatal3':
      return SeverityNumber.FATAL3;
    case 'fatal4':
      return SeverityNumber.FATAL4;
    case 'trace':
      return SeverityNumber.TRACE;
    case 'trace2':
      return SeverityNumber.TRACE2;
    case 'trace3':
      return SeverityNumber.TRACE3;
    case 'trace4':
      return SeverityNumber.TRACE4;
    default:
      return undefined;
  }
}

export function setLoggerProvider(
  config: ConfigurationModel,
  loggerProvider: LoggerProvider
): void {
  if (loggerProvider && loggerProvider.processors?.length > 0) {
    config.logger_provider = initializeDefaultLoggerProviderConfiguration();
    // Limits
    if (loggerProvider['limits']) {
      const attributeValueLengthLimit = getNumberFromConfigFile(
        loggerProvider['limits']['attribute_value_length_limit']
      );
      const attributeCountLimit = getNumberFromConfigFile(
        loggerProvider['limits']['attribute_count_limit']
      );
      if (attributeValueLengthLimit || attributeCountLimit) {
        if (attributeValueLengthLimit) {
          config.logger_provider.limits!.attribute_value_length_limit =
            attributeValueLengthLimit;
        }
        if (attributeCountLimit) {
          config.logger_provider.limits!.attribute_count_limit =
            attributeCountLimit;
        }
      }
    }

    // Processors
    for (let i = 0; i < loggerProvider['processors'].length; i++) {
      const processorType = Object.keys(loggerProvider['processors'][i])[0];
      if (processorType === 'batch') {
        const element = loggerProvider['processors'][i]['batch'];
        if (element) {
          const parsedExporter = parseConfigSpanOrLogRecordExporter(
            element['exporter'],
            ProviderType.LOGGER
          );
          const batchConfig: LogRecordProcessor = {
            batch: {
              schedule_delay:
                getNumberFromConfigFile(element['schedule_delay']) ?? 1000,
              export_timeout:
                getNumberFromConfigFile(element['export_timeout']) ?? 30000,
              max_queue_size:
                getNumberFromConfigFile(element['max_queue_size']) ?? 2048,
              max_export_batch_size:
                getNumberFromConfigFile(element['max_export_batch_size']) ??
                512,
              exporter: parsedExporter as LogRecordExporter,
            },
          };

          config.logger_provider.processors.push(batchConfig);
        }
      } else if (processorType === 'simple') {
        const element = loggerProvider['processors'][i]['simple'];
        if (element) {
          const parsedExporter = parseConfigSpanOrLogRecordExporter(
            element['exporter'],
            ProviderType.LOGGER
          );
          const simpleConfig: LogRecordProcessor = {
            simple: {
              exporter: parsedExporter,
            },
          };

          config.logger_provider.processors.push(simpleConfig);
        }
      }
    }

    // logger_configurator/development
    if (loggerProvider['logger_configurator/development']) {
      const defaultConfigDisabled = getBooleanFromConfigFile(
        loggerProvider['logger_configurator/development']['default_config']?.[
          'enabled'
        ]
      );
      if (defaultConfigDisabled || defaultConfigDisabled === false) {
        config.logger_provider['logger_configurator/development'] = {
          default_config: {
            enabled: defaultConfigDisabled,
          },
        };
      }

      if (
        loggerProvider['logger_configurator/development'].loggers &&
        loggerProvider['logger_configurator/development'].loggers.length > 0
      ) {
        const loggers = [];
        for (
          let i = 0;
          i < loggerProvider['logger_configurator/development'].loggers.length;
          i++
        ) {
          const logger =
            loggerProvider['logger_configurator/development'].loggers[i];
          let enabled = false;
          let traceBased;
          let minSeverity;
          if (logger['config']) {
            enabled =
              getBooleanFromConfigFile(logger['config']['enabled']) ?? false;
            traceBased = getBooleanFromConfigFile(
              logger['config']['trace_based']
            );
            if (logger['config']['minimum_severity']) {
              minSeverity = getSeverity(logger['config']['minimum_severity']);
            }
          }
          const name = getStringFromConfigFile(logger['name']);
          if (name) {
            const loggerNew: ExperimentalLoggerMatcherAndConfig = {
              name: name,
              config: {
                enabled: enabled,
              },
            };
            if (traceBased !== undefined) {
              loggerNew.config!.trace_based = traceBased;
            }
            if (minSeverity !== undefined) {
              loggerNew.config!.minimum_severity = minSeverity;
            }

            loggers.push(loggerNew);
          }
        }
        if (config.logger_provider['logger_configurator/development'] == null) {
          config.logger_provider['logger_configurator/development'] = {};
        }
        config.logger_provider['logger_configurator/development'].loggers =
          loggers;
      }
    }
  } else if (
    loggerProvider &&
    (loggerProvider.processors == null ||
      loggerProvider.processors.length === 0)
  ) {
    diag.warn('LoggerProvider must have at least one processor configured');
  }
}
